import React, { forwardRef, useMemo } from "react";
import { Grid } from "components/Grid";
import { useGridDetailsContextMenu } from "components/Grid/hooks/useGridDetailsContextMenu";
import { useTranslation } from "react-i18next";
import { netCall } from "utils/netCall";
import { ManagementApiUrl } from "environment/routeSettings";
import Spinner from "components/Spinner";
import CustomStore from "devextreme/data/custom_store";
import { generateFiltersByAcceptingCustomStringInput } from "components/Grid/utils";
import { DataGridRef } from "devextreme-react/data-grid";
import { TApartmentGridRow } from "components/Grid/grids/ApartmentsGrid/types";
import { useRegion_v2 } from "contexts/regions_v2";
import { GridTypes } from "components/Grid/grids/constants";
import { SortObjectByPropName } from "utils/arrayTools";
import { Enum2Array } from "utils/enumTools";
import { ApartmentType, WaterMeterAlarmsType } from "types/dto";

type TProps = {
  provinceId: string;
  cityId: string;
  jurisdictionId: string;
  actionButtons?: {
    icon: string;
    hint: string;
    onClick: (row: any) => void;
  }[];
  isLoading?: boolean;
  onRowClick?: (e: TApartmentGridRow) => void;
  /**
   * Adds right-click -> "view details" / "open in new tab" on each row.
   * Opt-in rather than default: this grid also renders inside chooser
   * modals, where navigating away would discard the user's selection.
   */
  enableDetailsContextMenu?: boolean;
};
const ApartmentsGrid = forwardRef<DataGridRef, TProps>((props, ref) => {
  const {
    jurisdictionId,
    provinceId,
    cityId,
    isLoading,
    actionButtons,
    onRowClick,
    enableDetailsContextMenu,
  } = props;

  // Called unconditionally (rules of hooks); the prop only decides whether
  // the resulting menu is handed to the grid.
  const detailsContextMenu = useGridDetailsContextMenu((row) =>
    row?.id ? `/dashboard/apartments/details/${row.id}` : undefined
  );
  const { t } = useTranslation();
  const { allJurisdictions } = useRegion_v2();

  const dataSource = useMemo(() => {
    return new CustomStore({
      key: "id",
      load: async (loadOption) => {
        if (loadOption.filter) {
          loadOption.filter = generateFiltersByAcceptingCustomStringInput(
            loadOption.filter
          );
        }

        const res = await netCall({
          method: "POST",
          endPoint: `${ManagementApiUrl()}/api/Apartment/GetApartments`,
          body: {
            loadOptions: { ...loadOption, requireTotalCount: false },
            provinceId: provinceId,
            cityId: cityId,
            jurisdictionId: jurisdictionId,
          },
        });
        return res.results;
      },
    });
  }, [provinceId, cityId, jurisdictionId]);

  const columns = useMemo(() => {
    const _columns: any[] = [
      {
        dataField: "id",
        caption: t("id"),
      },
      {
        dataField: "jurisdictionId",
        caption: t("region"),
        allowFiltering: false,
        allowSorting: false,
        calculateCellValue: (data: any) => {
          const jurisdiction = allJurisdictions.find(
            (item) => item.id === data?.jurisdictionId
          );
          if (jurisdiction) {
            return `${jurisdiction.provinceName} - ${jurisdiction.cityName} - ${jurisdiction.name}`;
          }
        },
      },
      {
        dataField: "address",
        caption: t("address"),
      },
      {
        dataField: "identifierId",
        caption: t("identifierId"),
      },
      {
        dataField: "apartmentType",
        caption: t("apartmentType"),
        lookup: {
          dataSource: SortObjectByPropName(Enum2Array(ApartmentType), "name"),
          valueExpr: "value",
          displayExpr: "name",
        },
      },
      {
        dataField: "numberOfFloor",
        caption: t("numberOfFloor"),
      },
      {
        dataField: "latitude",
        caption: t("latitude"),
      },
      {
        dataField: "longitude",
        caption: t("longitude"),
      },
    ];
    if (actionButtons && actionButtons.length > 0) {
      _columns.push({
        caption: t("actions"),
        visible: true,
        width: 110,
        buttons: actionButtons.map((button) => ({
          icon: button.icon,
          hint: button.hint,
          onClick: (e: any) => button.onClick(e.row.data),
        })),
      });
    }
    return _columns;
  }, [t, actionButtons]);

  return (
    <>
      <div className={"w-full flex gap-x flex-1 min-h-0"}>
        {isLoading && (
          <div className="z-[200] absolute bg-mofidGray-40 backdrop-blur-[2px] bg-opacity-30 top-0 left-0 bottom-0 right-0 flex justify-center items-center rounded-md">
            <p>{t("gettingInformation")}</p>
            <Spinner />
          </div>
        )}

        <Grid
          ref={ref}
          gridKey={"id"}
          dataSource={dataSource}
          storageKey={GridTypes.Apartments}
          columns={columns}
          rowContextMenu={
            enableDetailsContextMenu ? detailsContextMenu : undefined
          }
          hasSearchPanel={true}
          hasPagination={true}
          hasGroupingFeatures={false}
          hasFilterBuilder={true}
          onRowClick={(e) => {
            e?.data && onRowClick?.(e.data as TApartmentGridRow);
          }}
          height={"100%"}
        />
      </div>
    </>
  );
});

export default ApartmentsGrid;
